import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { OptimizedInput } from '@/components/ui/optimized-input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { GraduationCap, Play, X, ChevronLeft, ChevronRight, ShieldCheck } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import IntroSpinner from '@/components/IntroSpinner';
import { playMateChime } from '@/lib/mateSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROMO_VIDEOS = [
  { id: 'YTDrQyOZd9g', title: 'Conoce MATE' },
  { id: 'rkjsS5BX_qU', title: 'Funcionalidades' },
  { id: 'y7xe2NTWmSs', title: 'Cómo funciona' },
];

const VideoModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState(0);

  if (!open) return null;

  const video = PROMO_VIDEOS[current];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <div
        className="relative flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
          data-testid="video-modal-close"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Video */}
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-black" style={{ width: '340px', height: '604px' }}>
          <iframe
            key={video.id}
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-6 mt-5">
          <button
            onClick={() => setCurrent((p) => (p === 0 ? PROMO_VIDEOS.length - 1 : p - 1))}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
            data-testid="video-prev"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="flex gap-2">
            {PROMO_VIDEOS.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${i === current ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/60'}`}
                data-testid={`video-dot-${i}`}
              />
            ))}
          </div>

          <button
            onClick={() => setCurrent((p) => (p === PROMO_VIDEOS.length - 1 ? 0 : p + 1))}
            className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
            data-testid="video-next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <p className="text-white/70 text-xs mt-3">{current + 1} / {PROMO_VIDEOS.length}</p>
      </div>
    </div>
  );
};

const Login = ({ onLogin }) => {
  const { settings } = useSettings();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showVideos, setShowVideos] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Play the MATE signature sound on this guaranteed user gesture.
    playMateChime();
    setLoading(true);

    // If the user typed only the local part (no "@"), append the default
    // "@gmail.com" domain. If they typed a full address with "@", respect it.
    const typed = formData.email.trim();
    const emailToSend = typed.includes('@') ? typed : `${typed}@gmail.com`;

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: emailToSend,
        password: formData.password
      });

      // Cuenta con MFA activo: no hay token todavía, pedir el código de 6 dígitos.
      if (response.data.mfa_required) {
        setMfaChallenge(response.data.challenge);
        setLoading(false);
        return;
      }

      const { token, refresh_token, user } = response.data;

      // Clear form before navigating away
      setFormData({ email: '', password: '' });
      onLogin(token, user, refresh_token);
      toast.success('¡Bienvenido!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error en la autenticación');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/mfa/verify-login`, {
        challenge: mfaChallenge,
        code: mfaCode.trim()
      });
      const { token, refresh_token, user } = response.data;
      setFormData({ email: '', password: '' });
      setMfaChallenge(null);
      setMfaCode('');
      onLogin(token, user, refresh_token);
      toast.success('¡Bienvenido!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(to bottom right, #fff7ed, #f1f5f9)' }}>
      {showIntro && (
        <IntroSpinner logoUrl={settings.logo_url} onDone={() => setShowIntro(false)} />
      )}
      <Card className="w-full max-w-md p-8 bg-white border border-slate-200 rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.08)]" data-testid="login-card">
        <div className="flex flex-col items-center mb-8">
          {settings.logo_url ? (
            <div className={`relative w-48 h-48 rounded-2xl flex items-center justify-center mb-4 overflow-hidden ${showIntro ? '' : 'logo-stamp-shine'}`}>
              <img src={settings.logo_url} alt={settings.app_name} className="w-full h-full object-contain" />
              {!showIntro && (
                <img
                  src="/mate-check-green.png"
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-contain mate-check-paint pointer-events-none"
                  data-testid="logo-check-paint"
                />
              )}
            </div>
          ) : (
            <div className={`w-48 h-48 bg-orange-500 rounded-2xl flex items-center justify-center mb-4 ${showIntro ? '' : 'logo-stamp-shine'}`}>
              <GraduationCap className="w-24 h-24 text-white" />
            </div>
          )}
          <p className="text-sm text-slate-600 mb-2 text-center">{settings.app_subtitle || 'Sistema de Gestión Educativa'}</p>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {settings.app_name || 'Inicia Sesión'}
          </h1>
        </div>

        {mfaChallenge && (
          <form onSubmit={handleMfaSubmit} className="space-y-5" data-testid="mfa-form">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-3">
                <ShieldCheck className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-base font-semibold text-slate-900">Verificación en dos pasos</h2>
              <p className="text-sm text-slate-500 mt-1">Escribe el código de 6 dígitos de tu app de autenticación (o un código de respaldo).</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-code" className="text-sm font-medium text-slate-700">Código</Label>
              <input
                id="mfa-code"
                data-testid="mfa-code-input"
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                className="w-full h-11 rounded-lg border border-slate-200 px-3 tracking-widest text-center focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
              />
            </div>
            <Button
              type="submit"
              data-testid="mfa-verify-button"
              disabled={loading || mfaCode.trim().length < 6}
              className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg shadow-sm transition-all active:scale-95"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </Button>
            <button
              type="button"
              onClick={() => { setMfaChallenge(null); setMfaCode(''); }}
              className="w-full text-sm text-slate-500 hover:text-orange-600 transition-colors"
              data-testid="mfa-cancel-button"
            >
              Volver
            </button>
          </form>
        )}

        {!mfaChallenge && (
        <>
        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">Usuario</Label>
            <div className="relative">
              <OptimizedInput
                id="email"
                data-testid="email-input"
                type="text"
                name="login-user-nocache"
                autoComplete="off"
                placeholder="tu.usuario"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                debounceMs={50}
                className={`h-11 rounded-lg border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 ${formData.email.includes('@') ? '' : 'pr-24'}`}
              />
              {!formData.email.includes('@') && (
                <span
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400 select-none"
                  data-testid="email-domain-suffix"
                >
                  @gmail.com
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400">Solo escribe lo que va antes de @gmail.com. Si tu correo es de otro dominio, escríbelo completo.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-slate-700">Contraseña</Label>
            <OptimizedInput
              id="password"
              data-testid="password-input"
              type="password"
              name="login-pass-nocache"
              autoComplete="new-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              debounceMs={50}
              className="h-11 rounded-lg border-slate-200 focus:border-orange-500 focus:ring-orange-500/20"
            />
          </div>

          <Button
            type="submit"
            data-testid="submit-button"
            disabled={loading}
            className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg shadow-sm transition-all active:scale-95"
          >
            {loading ? 'Cargando...' : 'Iniciar Sesión'}
          </Button>
        </form>

        {/* Video promo link */}
        <button
          onClick={() => setShowVideos(true)}
          className="w-full mt-5 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-orange-600 transition-colors group"
          data-testid="open-videos-btn"
        >
          <Play className="w-4 h-4 group-hover:scale-110 transition-transform" />
          <span>¿Qué es MATE? Mira los videos</span>
        </button>

        <p className="text-center text-xs text-slate-400 mt-4" data-testid="new-user-hint">
          ¿Nuevo en MATE? Pide tu usuario y contraseña en tu escuela.
        </p>
        </>
        )}
      </Card>

      <VideoModal open={showVideos} onClose={() => setShowVideos(false)} />
    </div>
  );
};

export default Login;